"use client";

// The rich chat composer frame: TipTap input (skill "/" + artifact "@"
// mentions) with send/stop and an error banner. Fully functional standalone —
// every data-backed affordance appears only when the host supplies it:
//
//   skills/artifacts absent  → pickers never pop (plain text still works)
//   models absent            → no model selector button
//   uploads absent           → no attach button, no drag/drop, pastes inline
//   slots absent             → no extra toolbar buttons
//
// The private `@magnus/react` composer mounts its catalog-coupled pieces
// (model selector, approval/multiple-choice inputs, attachments, voice)
// around this same editor via its own frame; published consumers compose
// through the slot props below. Single source for npm + registry.
import { Button } from "@workspace/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { ArrowUpIcon, PaperclipIcon, SquareIcon } from "lucide-react";
import { type FormEvent, type ReactNode, type Ref, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ArtifactMentionOptions } from "./artifact-mention";
import { AttachmentsScroll, AttachmentTile, type ComposerUploadsSource, toAttachmentFilePart } from "./attachments";
import { PlusIcon } from "./icons";
import { ComposerLiveCallButton, type ComposerLiveCallSource, showsLiveCallButton } from "./live-call-button";
import { type ComposerModelsSource, ModelSelector } from "./model-selector";
import type { ArtifactPickerItem, SkillPickerItem } from "./refs";
import type { EditorPart } from "./serialize";
import type { SkillMentionOptions } from "./skill-mention";
import { TipTapSkillInput, type TipTapSkillInputHandle } from "./tiptap-input";
import { type ComposerAttachment, type UseAttachmentsResult, useAttachments } from "./use-attachments";
import { ComposerDictationButton, type ComposerDictationSource, useDictationTranscriptInsert } from "./voice";

/**
 * What a queue submit did. Drives the composer's live-region announcement,
 * so "cleared the draft while editing a queued item" can't report itself as
 * a successful queue.
 */
export type FollowUpQueueResult = "queued" | "removed" | "rejected";
export interface RichComposerProps {
  /** Host menu content rendered inside the block-owned "+" actions menu,
   *  below the "Attach files" item (C12 — capability toggles, connectors
   *  items, skill list). Absent → the menu carries block items only. */
  actionsMenuExtras?: ReactNode;
  artifactMentionOptions?: ArtifactMentionOptions;
  /** Artifacts offered by the "@" picker. Absent → no artifact mentions. */
  artifacts?: {
    items: ArtifactPickerItem[];
    loading?: boolean;
  };
  /**
   * Host-owned attachment state from {@link useAttachments}. Pass this when
   * the composer can unmount while tiles are staged — a layout swap, or a
   * prompt replacing the composer — because the hook's unmount sweep deletes
   * finalized upload rows it believes were abandoned. Absent → the composer
   * owns the state itself (`uploads` is all a standalone host needs).
   */
  attachments?: UseAttachmentsResult;
  /** Focus the editor on mount (C2). Defaults to true. */
  autoFocus?: boolean;
  className?: string;
  /** Tap-to-toggle dictation source (plan 027). Present → a mic button in
   *  the right cluster; finalized transcripts insert at the cursor.
   *  Absent → exactly today's layout. When BOTH this and
   *  `renderRightCluster` are supplied the source wins (the cluster slot
   *  is ignored) — the private layer never passes both.
   *  `useMagnusDictation()` returns a ready-made source. */
  dictation?: ComposerDictationSource;
  disabled?: boolean;
  /**
   * When true, Enter / Send while a turn is in flight enqueues a follow-up
   * instead of no-op. Stop stays Stop. Absent / false → today's no-op.
   */
  enableMessageQueue?: boolean;
  error?: Error;
  /** Extra content rendered between the attachment strip and the form
   *  (e.g. the flagship's workspace/context chip row). */
  extras?: ReactNode;
  /** Rendered inside the frame, above the editor. */
  headerSlot?: ReactNode;
  /** Seed a skill mention on mount (C4 — skill-start chiclet deep link). */
  initialSkill?: SkillPickerItem | null;
  /** Rendered in the footer's left cluster, beside the "+" actions menu. */
  leftTools?: ReactNode;
  /** Live voice call source (plan 042). Present → a call button takes
   *  Send's place while the composer is empty and idle. Absent → exactly
   *  today's layout. Ignored when `renderRightCluster` owns the cluster. */
  liveCall?: ComposerLiveCallSource;
  /** The model picker source. Absent → no model button. */
  models?: ComposerModelsSource;
  /** Bell mounted beside the model selector (C13 — NotifyWhenDone). */
  notifyBell?: ReactNode;
  /** Staged-tile activation (C11): the host opens its preview modal.
   *  Absent → staged tiles are static. */
  onAttachmentActivate?: (attachment: ComposerAttachment) => void;
  /** Fires when the staged attachment count changes. */
  onAttachmentCountChange?: (count: number) => void;
  /** Fires when a staged attachment's processing watch reports terminal
   *  failure (C12 promptReplacement seam — the host decides what text, if
   *  any, replaces the failed tile's mention). */
  onAttachmentProcessingFailed?: (uploadId: string, fileName: string) => void;
  /** Fires when a staged attachment's processing watch reports any
   *  terminal transition (C13 notify seam — sound/toast live host-side). */
  onAttachmentProcessingSettled?: (uploadId: string) => void;
  /** Escape while a queued item is in the composer — restore it in place. */
  onCancelQueueEdit?: () => void;
  /** Large plain-text pastes and pasted files land here when supplied
   *  (enables paste-to-attachment). Absent → pastes stay inline. */
  onPasteFiles?: (files: File[]) => void;
  /**
   * Enqueue a follow-up while a turn is in flight. Reports back what the
   * insert did so the composer announces it accurately: `"rejected"` keeps
   * the draft (queue full / declined), `"removed"` means an edited item was
   * cleared out of the queue rather than re-queued. Only called when
   * {@link RichComposerProps.enableMessageQueue} is true.
   */
  onQueue?: (parts: EditorPart[]) => FollowUpQueueResult | undefined;
  onSend: (parts: EditorPart[]) => boolean | Promise<boolean> | void | Promise<void>;
  /** Fires with the skill IDs currently present as mentions (C5 —
   *  skill-chip tracking). */
  onSkillsChange?: (ids: string[]) => void;
  onStop?: () => void;
  /** Fires on every content change with the canonical prompt string
   *  (C1 — `{{skill:id|name}}` tokens + text). Pair with `value` for a
   *  controlled draft. */
  onValueChange?: (value: string) => void;
  placeholder?: string;
  /**
   * Treat Enter / Submit as a queue insert even when the turn is idle
   * (a queued item is being edited in the composer).
   */
  preferQueue?: boolean;
  ref?: Ref<RichComposerHandle | null>;
  /** Replaces the default Send/Stop action button (C14 — the voice button
   *  cluster). Receives the default button so the host can keep it for the
   *  states the cluster doesn't cover. Absent → default button. */
  renderRightCluster?: (ctx: RichComposerRightClusterContext) => ReactNode;
  /** Wraps skill chips with a hover card. Absent → plain chips. */
  renderSkillHover?: (skillId: string, chip: ReactNode) => ReactNode;
  /** Rendered in the footer's right cluster, immediately before the model
   *  selector and ungapped from it (e.g. a context/usage meter). */
  rightTools?: ReactNode;
  skillMentionOptions?: SkillMentionOptions;
  /** Skills offered by the "/" picker. Absent → no skill mentions. */
  skills?: {
    items: SkillPickerItem[];
    loading?: boolean;
  };
  /** "submitted" | "streaming" show the Stop button. */
  status: "submitted" | "streaming" | "ready" | "error";
  /**
   * Extra send gate layered over the block's own (C8 — the flagship's
   * overfilled-context / host-owned holds). `reason` becomes the send
   * button's title.
   */
  submitDisabled?: {
    disabled: boolean;
    reason?: string;
  };
  /**
   * Attachments source (plan 023). Present → attach button, drag/drop, and
   * paste-to-attachment all stage tiles above the composer frame (matching
   * the flagship web composer); send gates until every tile finalizes and
   * appends native `file` parts after the text parts. Absent → no attach
   * affordances and pastes stay inline. When present it also takes over the
   * `onPasteFiles` path. `useMagnusUploads(chatId)` returns a ready-made
   * source.
   */
  uploads?: ComposerUploadsSource;
  /** Controlled draft value (canonical prompt string — C1). One-way sync:
   *  external changes are applied to the editor; editor changes flow out
   *  via `onValueChange`. Omit for an uncontrolled editor. */
  value?: string;
}
const EMPTY_SKILLS: SkillPickerItem[] = [];

/**
 * The imperative handle `RichComposer` exposes through `ref`: the editor
 * handle plus `addFiles`, so a host can stage attachments from OUTSIDE the
 * composer's own drop zone (surface-wide drag-and-drop, a "use this image"
 * button). Resolves to a no-op when no `uploads` source is wired.
 */
export type RichComposerHandle = TipTapSkillInputHandle & {
  /** Stage files exactly as a drop on the composer would. */
  addFiles: (files: File[] | FileList) => Promise<void>;
};

/**
 * A finalized staged upload mapped to the send shape — the fields a voice
 * cluster needs to submit a turn programmatically. Mirrors the private
 * `@magnus/react` `UploadedFile`.
 */
export interface ComposerUploadedFile {
  filename: string;
  id: string;
  mediaType: string;
  processingStatus?: string | null;
  size: number;
  storageKey: string;
  url: string;
}

/** Context handed to `renderRightCluster` (C14). */
export interface RichComposerRightClusterContext {
  canSend: boolean;
  /** True when there is text or at least one staged file (no upload /
   *  processing gating — see {@link canSubmitFinal}). */
  canSubmit: boolean;
  /** {@link canSubmit} AND all uploads finished AND nothing still
   *  processing. */
  canSubmitFinal: boolean;
  /** Clear staged attachments (e.g. after a successful voice turn). */
  clearFiles: () => void;
  /** The block's Send/Stop button — return it for states the host
   *  cluster doesn't cover. */
  defaultButton: ReactNode;
  disabled: boolean;
  /** Imperative editor handle (e.g. to stream a dictation transcript in). */
  editorRef: RefObject<TipTapSkillInputHandle | null>;
  isBusy: boolean;
  isUploading: boolean;
  /** Notify the controlled-value listener of programmatic edits. */
  onValueChange?: (value: string) => void;
  /** True while any staged upload's processing watch is non-terminal. */
  processingPending: boolean;
  /**
   * Set the editor content AND notify the controlled-value listener in one
   * step. Prefer this over `editorRef.setContent` + `onValueChange` for
   * streamed transcripts (dictation): the plain pair round-trips through
   * the parent's controlled `value`, and the sync effect can then re-apply
   * a stale value over keystrokes typed between transcript updates.
   */
  setContent: (text: string) => void;
  status: "submitted" | "streaming" | "ready" | "error";
  /** Staged, finalized attachments mapped to the send shape. */
  uploadedFilesToSend: ComposerUploadedFile[];
}

/**
 * TipTap-based composer frame. Enter sends, Shift+Enter inserts a newline,
 * "/" opens the skill picker (when `skills` is supplied), "@" the artifact
 * picker (when `artifacts` is supplied). While a turn is in flight the send
 * button becomes Stop.
 */
export function RichComposer({
  actionsMenuExtras,
  artifacts,
  artifactMentionOptions,
  attachments: providedAttachments,
  autoFocus,
  className,
  dictation,
  disabled = false,
  enableMessageQueue = false,
  error,
  extras,
  headerSlot,
  initialSkill,
  leftTools,
  liveCall,
  models,
  notifyBell,
  onAttachmentActivate,
  onAttachmentCountChange,
  onAttachmentProcessingFailed,
  onAttachmentProcessingSettled,
  onCancelQueueEdit,
  onQueue,
  onSend,
  onSkillsChange,
  onStop,
  onPasteFiles,
  onValueChange,
  placeholder = "Ask anything…",
  preferQueue = false,
  ref,
  renderRightCluster,
  rightTools,
  renderSkillHover,
  skills,
  skillMentionOptions,
  status,
  submitDisabled,
  uploads,
  value
}: RichComposerProps) {
  const editorRef = useRef<TipTapSkillInputHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [queueAnnouncement, setQueueAnnouncement] = useState("");
  const isBusy = status === "submitted" || status === "streaming";
  const queueEnabled = enableMessageQueue && !disabled;
  const onAttachmentProcessingSettledRef = useRef(onAttachmentProcessingSettled);
  onAttachmentProcessingSettledRef.current = onAttachmentProcessingSettled;
  const onAttachmentProcessingFailedRef = useRef(onAttachmentProcessingFailed);
  onAttachmentProcessingFailedRef.current = onAttachmentProcessingFailed;

  // Own the staging state only when the host doesn't. Passing no source keeps
  // the hook inert (no subscriptions, no unmount sweep) so the host's state is
  // the single owner — the hook is always called to keep hook order stable.
  const ownedAttachments = useAttachments(providedAttachments ? undefined : uploads, {
    onProcessingSettled: (uploadId, fileName, failed) => {
      onAttachmentProcessingSettledRef.current?.(uploadId);
      if (failed) {
        onAttachmentProcessingFailedRef.current?.(uploadId, fileName);
      }
    }
  });
  const attachments = providedAttachments ?? ownedAttachments;

  // Staged-count changes (the flagship sizes its workspace chip row and drop
  // affordances off this).
  const onAttachmentCountChangeRef = useRef(onAttachmentCountChange);
  onAttachmentCountChangeRef.current = onAttachmentCountChange;
  const stagedCount = attachments.files.length;
  useEffect(() => {
    onAttachmentCountChangeRef.current?.(stagedCount);
  }, [stagedCount]);

  // Latest-value refs so the imperative handle and the native drop listeners
  // (both created once) always reach the current attachments source.
  const uploadsActiveRef = useRef(Boolean(uploads));
  uploadsActiveRef.current = Boolean(uploads);
  const addFilesRef = useRef(attachments.addFiles);
  addFilesRef.current = attachments.addFiles;

  // Expose the editor handle to the parent without a second ref plumbing —
  // extended with `addFiles` so hosts can stage attachments from outside the
  // composer's own drop zone (see {@link RichComposerHandle}).
  const setEditorRef = useCallback((editorHandle: TipTapSkillInputHandle | null) => {
    editorRef.current = editorHandle;
    const handle: RichComposerHandle | null = editorHandle ? {
      ...editorHandle,
      addFiles: files => {
        if (!uploadsActiveRef.current) {
          return Promise.resolve();
        }
        return addFilesRef.current(files);
      }
    } : null;
    if (typeof ref === "function") {
      ref(handle);
    } else if (ref) {
      ref.current = handle;
    }
  }, [ref]);

  // Drag/drop staging — native listeners on the drop-zone wrapper (JSX drag
  // handlers trip the a11y linter on non-interactive elements; the private
  // composer does the same). The zone wraps BOTH the attachment strip and
  // the form, like the flagship's `Composer.Frame`, so dropping onto staged
  // tiles also stages. Only intercepts file drags when a source is present;
  // otherwise the browser default (and text drags) pass through untouched.
  const dropZoneRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const zone = dropZoneRef.current;
    if (!zone) {
      return;
    }
    const handleDragOver = (event: DragEvent) => {
      if (uploadsActiveRef.current && event.dataTransfer?.types.includes("Files")) {
        event.preventDefault();
        setIsDragging(true);
      }
    };
    const handleDragLeave = (event_0: DragEvent) => {
      if (!zone.contains(event_0.relatedTarget as Node | null)) {
        setIsDragging(false);
      }
    };
    const handleDrop = (event_1: DragEvent) => {
      if (!uploadsActiveRef.current || !event_1.dataTransfer?.types.includes("Files")) {
        return;
      }
      event_1.preventDefault();
      setIsDragging(false);
      // Fire-and-forget: addFiles surfaces failures as toasts itself.
      addFilesRef.current(event_1.dataTransfer.files).catch(() => undefined);
    };
    zone.addEventListener("dragover", handleDragOver);
    zone.addEventListener("dragleave", handleDragLeave);
    zone.addEventListener("drop", handleDrop);
    return () => {
      zone.removeEventListener("dragover", handleDragOver);
      zone.removeEventListener("dragleave", handleDragLeave);
      zone.removeEventListener("drop", handleDrop);
    };
  }, []);
  const hasFiles = attachments.files.length > 0;
  const uploadsSettled = !attachments.isUploading && (!hasFiles || attachments.allUploaded) &&
  // C10: with a processing watch wired, send holds until every staged
  // upload's pipeline reports terminal (ADR-055 text-ready gate).
  attachments.allProcessingSettled;
  const processingPending = hasFiles && !attachments.allProcessingSettled;
  const canSubmit = !isEmpty || hasFiles;
  const canSubmitFinal = canSubmit && uploadsSettled;
  const canSend = canSubmitFinal && !disabled && !(submitDisabled?.disabled ?? false);
  const queueEditActive = preferQueue && queueEnabled;
  // Editing a queued item makes an empty composer actionable — clearing the
  // draft removes that item — so the button drops the non-empty requirement
  // there and follows the same rule as Enter. Everything else `canSend`
  // gates (disabled host, uploads still in flight) still applies.
  const canQueueSubmit = queueEditActive ? uploadsSettled && !disabled && !(submitDisabled?.disabled ?? false) : canSend;

  // Synced programmatic set: write the editor and notify the controlled-
  // value listener in one step, so a streamed dictation transcript can't
  // round-trip through the parent's `value` and revert interleaved
  // keystrokes (mirrors the private composer's `setSyncedContent`).
  const setSyncedContent = useCallback((text: string) => {
    editorRef.current?.setContent(text);
    onValueChange?.(text);
  }, [onValueChange]);

  // Plan 027: finalized dictation transcripts land at the cursor. The
  // insert goes through the editor (not `value`) so it composes with
  // mention chips and fires the canonical onValueChange via onUpdate.
  useDictationTranscriptInsert(dictation, editorRef);

  // Native listener (same reason as the file drop zone): a JSX onKeyDown on
  // the form trips the a11y lint, and a window listener would steal Escape
  // from mention pickers, menus, and dialogs.
  useEffect(() => {
    const form = formRef.current;
    if (!form || !(preferQueue && onCancelQueueEdit)) {
      return;
    }
    const onKeyDown = (event_2: KeyboardEvent) => {
      if (event_2.key !== "Escape" || event_2.defaultPrevented) {
        return;
      }
      event_2.preventDefault();
      onCancelQueueEdit();
    };
    form.addEventListener("keydown", onKeyDown);
    return () => form.removeEventListener("keydown", onKeyDown);
  }, [onCancelQueueEdit, preferQueue]);
  const uploadedFilesToSend = useMemo<ComposerUploadedFile[]>(() => attachments.uploadedFiles.flatMap(attachment => {
    const uploaded = attachment.uploaded;
    if (!uploaded) {
      return [];
    }
    return [{
      id: uploaded.id,
      filename: uploaded.filename,
      mediaType: uploaded.mediaType,
      size: attachment.file.size,
      url: uploaded.url,
      storageKey: uploaded.storageKey,
      processingStatus: uploaded.processingStatus
    }];
  }), [attachments.uploadedFiles]);
  const enqueueInsteadOfSend = queueEnabled && (isBusy || preferQueue);

  // Clear first, then set on a microtask: an aria-live region only speaks on
  // a change, so queueing two identical follow-ups in a row would otherwise
  // announce nothing the second time.
  const announce = useCallback((message: string) => {
    setQueueAnnouncement("");
    queueMicrotask(() => setQueueAnnouncement(message));
  }, []);
  const handleSubmit = useCallback((event_3: FormEvent) => {
    event_3.preventDefault();
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const parts = editor.getParts();
    // Text parts first, then one native `file` part per finalized
    // attachment — the same ordering the flagship web app submits.
    const fileParts = attachments.uploadedFiles.map(toAttachmentFilePart);
    const allParts = [...parts, ...fileParts];
    if (allParts.length === 0 && !preferQueue) {
      return;
    }
    if (!canQueueSubmit) {
      return;
    }
    if (isBusy && !queueEnabled) {
      return;
    }
    if (enqueueInsteadOfSend) {
      const result = onQueue?.(allParts);
      if (result === "rejected") {
        announce("Queue is full");
        return;
      }
      editor.clear();
      onValueChange?.("");
      attachments.clearFiles();
      editor.focus();
      announce(result === "removed" ? "Follow-up removed" : "Follow-up queued");
      return;
    }
    // E9: clear only once the host accepts the send; a declined submit
    // (resolves false) keeps the draft and the staged tiles.
    Promise.resolve(onSend(allParts)).then(accepted => {
      if (accepted === false) {
        return;
      }
      editor.clear();
      // `editor.clear()` emits the empty value only while the editor is
      // alive. A host that accepted asynchronously may have already
      // swapped the layout (empty → active) and unmounted it, so notify
      // the controlled-value listener directly or the sent draft stays in
      // the host's state and gets re-applied to the next editor.
      onValueChange?.("");
      attachments.clearFiles();
    }).catch(() => undefined);
  }, [announce, attachments, canQueueSubmit, enqueueInsteadOfSend, isBusy, onQueue, onSend, onValueChange, preferQueue, queueEnabled]);

  // All three sit at the cluster's base radius rather than the tighter clamp
  // `size="icon-sm"` would apply, and submit and queue share their slot with
  // the call button, so they take the same `composer-action` pair. See
  // `ComposerLiveCallButton` for the radius caveat.
  const stopButton = <Button aria-label="Stop" className="rounded-lg" onClick={onStop} size="icon-sm" type="button" variant="outline">
      <SquareIcon className="size-3.5 fill-current" />
    </Button>;
  const submitButton = <Button aria-label="Submit" className="rounded-lg bg-composer-action text-composer-action-foreground hover:bg-composer-action/90" disabled={!canSend} size="icon-sm" title={submitDisabled?.disabled ? submitDisabled.reason : undefined} type="submit">
      <ArrowUpIcon className="size-4" />
    </Button>;
  const queueButton = <Button aria-label="Send after this reply" className="rounded-lg bg-composer-action text-composer-action-foreground hover:bg-composer-action/90" disabled={!canQueueSubmit} size="icon-sm" title="Send after this reply" type="submit">
      <ArrowUpIcon className="size-4" />
    </Button>;
  const defaultActionButton = isBusy ? queueEnabled && canQueueSubmit ? <>
        {queueButton}
        {stopButton}
      </> : stopButton : queueEditActive ? queueButton : submitButton;
  const actionButton = liveCall && !(isBusy || queueEditActive || canSubmit) && showsLiveCallButton(liveCall) ? <ComposerLiveCallButton disabled={disabled} liveCall={liveCall} /> : defaultActionButton;
  const resolvedPlaceholder = queueEditActive ? "Edit follow-up…" : queueEnabled && isBusy ? "Add a follow-up…" : placeholder;
  return <div
  // The inset is a CONTAINER query, not a viewport one. px-0 is only safe
  // when the chat column is wider than the frame's 48rem cap — then
  // `mx-auto` supplies the breathing room. That used to be inferred from
  // `sm:`, which holds in a full-width chat but not in a narrow rail on a
  // wide screen (Create's chat beside an open preview): there the frame
  // went edge-to-edge with its border riding the panel boundary. Querying
  // the column itself (the @container wrappers in chat.tsx) keeps the 8px
  // inset whenever the frame would otherwise touch the edges. 50rem = the
  // 48rem cap plus 1rem of visible margin per side. With no @container
  // ancestor the query never matches and the px-2 fallback stays — the
  // safe direction.
  className={cn("mx-auto w-full max-w-3xl px-2 pb-4 @[50rem]:px-0", className)}>
      <div aria-live="polite" className="sr-only">
        {queueAnnouncement}
      </div>
      {error && <div className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive text-sm">
          {error.message}
        </div>}
      {/* Drop-zone frame: attachment strip ABOVE the input frame, matching
          the flagship composer's `Composer.Frame` layout (strip, then
          form) — not a strip inside the frame. */}
      <div className="relative" ref={dropZoneRef}>
        {isDragging && <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-primary/5">
            <div className="flex items-center gap-2 text-primary">
              <PaperclipIcon className="size-5" />
              <span className="font-medium">Drop files here</span>
            </div>
          </div>}
        {hasFiles && <AttachmentsScroll>
            {attachments.files.map(attachment_0 => <AttachmentTile key={attachment_0.id} mediaType={attachment_0.file.type} name={attachment_0.file.name} onActivate={onAttachmentActivate ? () => onAttachmentActivate(attachment_0) : undefined} onRemove={() => attachments.removeFile(attachment_0.id)} onRetry={() => attachments.retryUpload(attachment_0.id)} previewUrl={attachment_0.previewUrl} processingStatus={attachment_0.uploaded?.processingStatus} status={attachment_0.status === "uploading" || attachment_0.status === "error" ? attachment_0.status : undefined} />)}
          </AttachmentsScroll>}
        {extras}
        {/* A failed tile blocks send (`allUploaded` is false); say so
            instead of leaving the button inexplicably dead. */}
        {attachments.hasErrors && <p className="mb-2 px-3 text-destructive text-xs" role="status">
            An attachment failed to upload. Retry or remove it to send.
          </p>}
        {/* Frame treatment, matching the flagship composer's: `bg-composer` is
            the plane, no shadow (the dock's own edge already separates it from
            the transcript), and no focus treatment on the frame — the caret is
            the focus indicator for a text surface, and the interactive children
            carry their own rings. */}
        <form className="relative rounded-2xl border border-input bg-composer" onSubmit={handleSubmit} ref={formRef}>
          {headerSlot}
          <TipTapSkillInput artifactMentionOptions={artifactMentionOptions} artifactsLoading={artifacts?.loading} autoFocus={autoFocus} availableArtifacts={artifacts?.items} availableSkills={skills?.items ?? EMPTY_SKILLS} disabled={disabled} enableArtifactMentions={Boolean(artifacts)} initialSkill={initialSkill} onContentChange={empty => setIsEmpty(empty)} onPasteFiles={uploads ? files_0 => {
          attachments.addFiles(files_0).catch(() => undefined);
        } : onPasteFiles} onSkillsChange={onSkillsChange} onValueChange={onValueChange} placeholder={resolvedPlaceholder} ref={setEditorRef} renderSkillHover={renderSkillHover} skillMentionOptions={skillMentionOptions} skillsLoading={skills?.loading} value={value} />
          {/* Wraps because this row has a hard minimum width — two 32px
              buttons, the "+", and any host-supplied chips, several of which
              are deliberately `shrink-0`. Without wrapping, a chat column
              narrower than that sum pushes the whole cluster past the frame's
              rounded border instead of reflowing (Create's chat rail beside an
              open preview). Shrink first (see `min-w-0` below), wrap second;
              at normal widths neither engages. */}
          <div className="flex flex-wrap items-center gap-1 p-2 pt-0">
            {(uploads || actionsMenuExtras) && <DropdownMenu>
                <DropdownMenuTrigger render={<Button aria-label="Open composer actions" className="size-8 rounded-lg" disabled={disabled} size="icon" variant="ghost" />}>
                  <PlusIcon className="size-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {uploads && <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                      <PaperclipIcon className="size-4" />
                      Attach files
                    </DropdownMenuItem>}
                  {actionsMenuExtras}
                </DropdownMenuContent>
              </DropdownMenu>}
            {uploads && <input accept={uploads.accept.join(",")} aria-hidden className="hidden" multiple onChange={event_4 => {
            if (event_4.target.files?.length) {
              attachments.addFiles(event_4.target.files).catch(() => undefined);
            }
            // Reset so picking the same file twice re-fires onChange.
            event_4.target.value = "";
          }} ref={fileInputRef} tabIndex={-1} type="file" />}
            {leftTools}
            {/* `min-w-0` is what keeps a narrow composer inside its border:
                the footer does not wrap, so without it this cluster cannot
                shrink below the model name's own width and spills past the
                rounded frame (seen first in Create's chat rail beside an open
                preview). The model button truncates in that state. */}
            <div className="ml-auto flex min-w-0 items-center gap-1">
              {/* Ungapped pair: the context meter reads as a prefix of the
                  model button rather than a separate control. Both live in the
                  right cluster (ahead of the voice/submit area) in every mode
                  so the user never loses sight of — or the ability to switch —
                  the model; a switch applies on the next assistant turn. */}
              {(rightTools || models) && <div className="flex min-w-0 items-center gap-0">
                  {rightTools}
                  {models && <ModelSelector models={models} />}
                </div>}
              {notifyBell}
              {dictation && <ComposerDictationButton dictation={dictation} disabled={disabled} />}
              {/* A dictation source wins over the C14 cluster slot — the
                  private layer never passes both. */}
              {dictation ? actionButton : renderRightCluster ? renderRightCluster({
              canSend,
              canSubmit,
              canSubmitFinal,
              clearFiles: attachments.clearFiles,
              defaultButton: defaultActionButton,
              disabled,
              editorRef,
              isBusy,
              isUploading: attachments.isUploading,
              onValueChange,
              processingPending,
              setContent: setSyncedContent,
              status,
              uploadedFilesToSend
            }) : actionButton}
            </div>
          </div>
        </form>
      </div>
    </div>;
}